import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const SCHEDULES_MANAGEMENT_TOOLS_METADATA = [
  {
    name: "create_schedule",
    description:
      "Create a schedule that runs this agent at specified times. Schedules are user-specific: each user can only view and manage their own schedules. When the schedule triggers, it runs this agent with the specified prompt. Pass podId to attach the schedule to a Pod so its runs land there. Limit: 20 schedule creations per user per day.",
    schema: {
      name: z
        .string()
        .max(255)
        .describe(
          "A short, descriptive name for the schedule (max 255 chars). Examples: 'Daily email summary', 'Weekly PR review', 'Morning standup prep'. Schedule name MUST be unique."
        ),
      podId: z
        .string()
        .optional()
        .describe(
          "Optional Pod ID (sId) to attach this schedule to, so its runs land in that Pod. Omit for a schedule not tied to a Pod."
        ),
      schedule: z
        .string()
        .describe(
          "When to run, in natural language. Examples: 'every weekday at 9am', 'every Monday morning', 'daily at 8am', 'first day of each month at noon', 'every Friday at 5pm'"
        ),
      prompt: z
        .string()
        .optional()
        .describe(
          "What the agent should do when the schedule runs. Examples: 'Summarize my emails from yesterday', 'Show PRs that need my review', 'Generate a weekly status report'"
        ),
      timezone: z
        .string()
        .optional()
        .describe(
          "IANA timezone for the schedule. Examples: 'Europe/Paris', 'America/New_York', 'Asia/Tokyo'. If not provided, uses user's timezone from context."
        ),
    },
    stake: "high",
    displayLabels: {
      running: "Creating schedule",
      done: "Create schedule",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
  {
    name: "list_schedules",
    description:
      "List all schedules for this agent and the current user. Each entry shows the Pod it is attached to, if any.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Listing schedules",
      done: "List schedules",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "disable_schedule",
    description: "Disable a schedule.",
    schema: {
      scheduleId: z
        .string()
        .describe("The schedule ID (get this from list_schedules)"),
    },
    stake: "high",
    displayLabels: {
      running: "Disabling schedule",
      done: "Disable schedule",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const SCHEDULES_MANAGEMENT_SERVER = {
  serverInfo: {
    name: "schedules_management" as const,
    version: "1.0.0",
    description: "Create schedules to automate recurring tasks.",
    authorization: null,
    icon: "ActionTimeIcon" as const,
    documentationUrl: null,
  },
  tools: SCHEDULES_MANAGEMENT_TOOLS_METADATA,
} as const satisfies ServerMetadata;
